const API = process.env.REACT_APP_API_URL || '/api';

function getToken() {
  return localStorage.getItem('token');
}

function tokenExpirado() {
  const token = getToken();
  if (!token) return true;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 < Date.now();
  } catch {
    return true;
  }
}

export function verificarSessao() {
  if (tokenExpirado()) {
    dispararSessaoExpirada();
    return false;
  }
  return true;
}

const MENSAGENS_ERRO = {
  400: 'Dados inválidos. Verifique os campos e tente novamente.',
  401: 'Sessão expirada. Faça login novamente.',
  402: 'Assinatura vencida. Realize o pagamento para continuar.',
  403: 'Você não tem permissão para realizar esta ação.',
  404: 'Registro não encontrado.',
  409: 'Já existe um registro com esses dados.',
  422: 'Dados inválidos. Verifique os campos.',
  429: 'Muitas tentativas. Aguarde alguns minutos e tente novamente.',
  500: 'Erro interno do servidor. Tente novamente em instantes.',
  503: 'Serviço indisponível. Tente novamente em instantes.',
};

function criarErro(status, mensagem) {
  const erro = new Error(mensagem);
  erro.status = status;
  return erro;
}

/** Fazenda ativa da sessão: toda requisição opera no escopo dela (multi-fazenda). */
export function getFazendaAtivaId() {
  return localStorage.getItem('fazendaAtivaId');
}

export function setFazendaAtivaId(id) {
  if (id == null) localStorage.removeItem('fazendaAtivaId');
  else localStorage.setItem('fazendaAtivaId', String(id));
}

async function request(url, options = {}) {
  const token = getToken();
  const headers = { 'Content-Type': 'application/json', ...options.headers };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  // Fazenda ativa por requisição. O backend valida o vínculo ATIVO nela e
  // resolve o papel (ADMIN/MEMBRO) do usuário naquela fazenda.
  const fazendaAtivaId = getFazendaAtivaId();
  if (fazendaAtivaId) headers['X-Fazenda-Id'] = fazendaAtivaId;

  const res = await fetch(API + url, { ...options, headers });

  if (!res.ok) {
    let mensagem = MENSAGENS_ERRO[res.status];
    try {
      const body = await res.json();
      const bodyMsg = body?.erro || body?.message;
      if (bodyMsg) mensagem = bodyMsg;
    } catch {}

    // 401 com token = sessão expirada. Sem token, é falha de credencial
    // no login e quem trata é a própria tela.
    if (res.status === 401 && token) dispararSessaoExpirada();

    // 402 = assinatura vencida
    if (res.status === 402) dispararAssinaturaVencida();

    throw criarErro(res.status, mensagem || MENSAGENS_ERRO[500]);
  }
  return res.status === 204 ? null : res.json();
}

function dispararSessaoExpirada() {
  localStorage.clear();
  sessionStorage.clear();
  window.dispatchEvent(new CustomEvent('sessao-expirada'));
}

function dispararAssinaturaVencida() {
  window.dispatchEvent(new CustomEvent('assinatura-vencida'));
}

export const api = {
  // Fazendas
  getFazendaMe: () => request('/fazendas/me'),
  getAssinatura: () => request('/fazendas/assinatura'),
  getPlanos: () => request('/fazendas/planos'),
  getAdminDashboard: () => request('/admin/dashboard'),
  getSouMaster: () => request('/admin/sou-master'),
  liberarFazendaUmMes: (id) => request(`/admin/fazendas/${id}/liberar`, { method: 'POST' }),
  // Multi-fazenda: lista as fazendas do usuário (para o seletor) e marca a ativa.
  getMinhasFazendas: () => request('/fazendas/minhas'),
  ativarFazenda: (fazendaId) => request(`/fazendas/ativar/${fazendaId}`, { method: 'POST' }),

  // Health
  healthCheck: () => request('/healthy'),

  // Auth
  login: (email, senha) => {
    const source = (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone || window.location.search.includes('source=pwa')) ? 'pwa' : 'browser';
    return request('/auth/login', { method: 'POST', body: JSON.stringify({ email, senha, source }) });
  },
  criarFazenda: (data) => {
    const source = (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone || window.location.search.includes('source=pwa')) ? 'pwa' : 'browser';
    return request('/auth/criar-fazenda', { method: 'POST', body: JSON.stringify({ ...data, source }) });
  },
  entrarComCodigo: (data) => request('/auth/entrar-com-codigo', { method: 'POST', body: JSON.stringify(data) }),
  esqueciSenha: (email) =>
    request('/auth/esqueci-senha', { method: 'POST', body: JSON.stringify({ email }) }),
  redefinirSenha: (token, email, novaSenha) =>
    request('/auth/redefinir-senha', { method: 'POST', body: JSON.stringify({ token, email, novaSenha }) }),

  // Usuários
  getUsuarios: () => request('/usuarios'),
  getUsuariosPendentes: () => request('/usuarios/pendentes'),
  aprovarUsuario: (id) => request(`/usuarios/${id}/aprovar`, { method: 'PATCH' }),
  excluirUsuario: (id) => request(`/usuarios/${id}`, { method: 'DELETE' }),
  cadastrarEmail: (email) => request('/usuarios/meu-email', { method: 'PATCH', body: JSON.stringify({ email }) }),

  // Armazéns
  getArmazens: () => request('/armazens'),
  criarArmazem: (data) => request('/armazens', { method: 'POST', body: JSON.stringify(data) }),
  editarArmazem: (id, data) => request(`/armazens/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  excluirArmazem: (id) => request(`/armazens/${id}`, { method: 'DELETE' }),

  // Lavouras
  getLavouras: () => request('/lavouras'),
  criarLavoura: (data) => request('/lavouras', { method: 'POST', body: JSON.stringify(data) }),
  editarLavoura: (id, data) => request(`/lavouras/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  excluirLavoura: (id) => request(`/lavouras/${id}`, { method: 'DELETE' }),

  // Safras
  getSafras: () => request('/safras'),
  criarSafra: (data) => request('/safras', { method: 'POST', body: JSON.stringify(data) }),
  editarSafra: (id, data) => request(`/safras/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  excluirSafra: (id) => request(`/safras/${id}`, { method: 'DELETE' }),
  fecharSafra: (id) => request(`/safras/${id}/fechar`, { method: 'PATCH' }),
  reabrirSafra: (id) => request(`/safras/${id}/reabrir`, { method: 'PATCH' }),
  getLavourasDaSafra: (safraId) => request(`/safras/${safraId}/lavouras`),
  getLavourasAtivasDaSafra: (safraId) => request(`/safras/${safraId}/lavouras/ativas`),
  vincularLavoura: (safraId, data) => request(`/safras/${safraId}/lavouras`, { method: 'POST', body: JSON.stringify(data) }),
  editarSafraLavoura: (id, data) => request(`/safras/lavouras/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  excluirSafraLavoura: (id) => request(`/safras/lavouras/${id}`, { method: 'DELETE' }),

  // Cargas
  criarCarga: (data) => request('/cargas', { method: 'POST', body: JSON.stringify(data) }),
  listarCargas: (page = 0, size = 20) => request(`/cargas/todas?page=${page}&size=${size}`),
  atualizarCarga: (id, data) => request(`/cargas/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  excluirCarga: (id) => request(`/cargas/${id}`, { method: 'DELETE' }),
  getCargasPorPeriodo: (inicio, fim) => request(`/cargas?inicio=${inicio}&fim=${fim}`),
  getCargasPorArmazem: (armazemId) => request(`/cargas/armazem/${armazemId}`),
  getCargasPorSafra: (safraId) => request(`/cargas/safra/${safraId}`),
  getResumoSafraLavoura: (safraLavouraId) => request(`/cargas/safra-lavoura/${safraLavouraId}/resumo`),

  // Vendas
  criarVenda: (data) => request('/vendas', { method: 'POST', body: JSON.stringify(data) }),
  listarVendas: () => request('/vendas'),
  atualizarVenda: (id, data) => request(`/vendas/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  excluirVenda: (id) => request(`/vendas/${id}`, { method: 'DELETE' }),
  getEstoqueArmazem: (armazemId, safraId) => request(`/vendas/estoque?${armazemId ? `armazemId=${armazemId}&` : ''}safraId=${safraId}`),
  getUnidadesSafra: (safraId) => request(`/vendas/unidades-safra/${safraId}`),

  // Insumos
  getLotesEstoque: (insumoId) => request(`/insumos/${insumoId}/lotes`),
  getEstoqueInsumos: () => request('/insumos/estoque'),
  getInsumos: () => request('/insumos'),
  buscarInsumos: (termo, categoria) => request(`/insumos/buscar?termo=${encodeURIComponent(termo)}${categoria ? `&categoria=${categoria}` : ''}`),
  criarInsumo: (data) => request('/insumos', { method: 'POST', body: JSON.stringify(data) }),
  editarInsumo: (id, data) => request(`/insumos/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  getCompras: () => request('/insumos/compras'),
  getComprasPorInsumo: (insumoId) => request(`/insumos/${insumoId}/compras`),
  getHistoricoPrecos: (insumoId) => request(`/insumos/${insumoId}/historico-precos`),
  registrarCompra: (data) => request('/insumos/compras', { method: 'POST', body: JSON.stringify(data) }),
  editarCompra: (id, data) => request(`/insumos/compras/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  excluirCompra: (id) => request(`/insumos/compras/${id}`, { method: 'DELETE' }),
  ajustarQuantidadeCompra: (id, quantidadeDisponivel) => request(`/insumos/compras/${id}/quantidade`, { method: 'PATCH', body: JSON.stringify({ quantidadeDisponivel }) }),
  ajustarEstoque: (insumoId, novaQuantidade) => request(`/insumos/${insumoId}/estoque`, { method: 'PATCH', body: JSON.stringify({ novaQuantidade }) }),

  // Custos (lançamentos por talhão)
  getCustos: (safraLavouraId) => request(`/custos/safra-lavoura/${safraLavouraId}`),
  registrarCusto: (data) => request('/custos', { method: 'POST', body: JSON.stringify(data) }),
  registrarCustoLote: (data) => request('/custos/lote', { method: 'POST', body: JSON.stringify(data) }),
  excluirCusto: (id, devolverEstoque = false) => request(`/custos/${id}?devolverEstoque=${devolverEstoque}`, { method: 'DELETE' }),
  editarCusto: (id, data) => request(`/custos/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  // Lançamento inteiro (todas as linhas do lote): editar cabeçalho / excluir tudo.
  editarLancamento: (loteId, data) => request(`/custos/lote/${loteId}`, { method: 'PUT', body: JSON.stringify(data) }),
  excluirLancamento: (loteId, devolverEstoque = false) => request(`/custos/lote/${loteId}?devolverEstoque=${devolverEstoque}`, { method: 'DELETE' }),
  getCustosSafraLavoura: (safraLavouraId) => request(`/custos/safra-lavoura/${safraLavouraId}/custos`),
  getRelatorioCustosLavoura: (safraLavouraId, inicio, fim) => {
    const qs = new URLSearchParams();
    if (inicio) qs.set('inicio', inicio);
    if (fim) qs.set('fim', fim);
    const sufixo = qs.toString() ? `?${qs.toString()}` : '';
    return request(`/custos/relatorio/safra-lavoura/${safraLavouraId}${sufixo}`);
  },

  // Pagamento
  criarCheckout: (successUrl, cancelUrl) => request('/pagamento/checkout', { method: 'POST', body: JSON.stringify({ successUrl, cancelUrl }) }),

  // Resultado
  getResultadoSafra: (safraId) => request(`/resultado/safra/${safraId}`),
  getHistoricoProdutividade: () => request('/resultado/historico-produtividade'),

  // Livro Caixa
  getLivroCaixa: (ano) => request(`/livro-caixa?ano=${ano}`),

  // Contas a pagar
  getContasAPagar: () => request('/contas-a-pagar'),
  getAvisosContasAPagar: () => request('/contas-a-pagar/avisos'),
  getCountAvisosContasAPagar: () => request('/contas-a-pagar/avisos/count'),
  criarContaAPagar: (data) => request('/contas-a-pagar', { method: 'POST', body: JSON.stringify(data) }),
  editarContaAPagar: (id, data) => request(`/contas-a-pagar/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  marcarContaPaga: (id, data) => request(`/contas-a-pagar/${id}/pagar`, {
    method: 'PATCH',
    ...(data ? { body: JSON.stringify(data) } : {}),
  }),
  reabrirContaAPagar: (id) => request(`/contas-a-pagar/${id}/reabrir`, { method: 'PATCH' }),
  excluirContaAPagar: (id) => request(`/contas-a-pagar/${id}`, { method: 'DELETE' }),

  // Contas a receber
  getContasAReceber: () => request('/contas-a-receber'),
  criarContaAReceber: (data) => request('/contas-a-receber', { method: 'POST', body: JSON.stringify(data) }),
  editarContaAReceber: (id, data) => request(`/contas-a-receber/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  marcarContaRecebida: (id) => request(`/contas-a-receber/${id}/receber`, { method: 'PATCH' }),
  reabrirContaAReceber: (id) => request(`/contas-a-receber/${id}/reabrir`, { method: 'PATCH' }),
  excluirContaAReceber: (id) => request(`/contas-a-receber/${id}`, { method: 'DELETE' }),

  // Maquinário
  getMaquinarios: () => request('/maquinario'),
  criarMaquinario: (data) => request('/maquinario', { method: 'POST', body: JSON.stringify(data) }),
  editarMaquinario: (id, data) => request(`/maquinario/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  excluirMaquinario: (id) => request(`/maquinario/${id}`, { method: 'DELETE' }),
  getManutencoesMaquinario: () => request('/maquinario/manutencoes'),
  criarManutencaoMaquinario: (data) => request('/maquinario/manutencoes', { method: 'POST', body: JSON.stringify(data) }),
  editarManutencaoMaquinario: (id, data) => request(`/maquinario/manutencoes/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  excluirManutencaoMaquinario: (id) => request(`/maquinario/manutencoes/${id}`, { method: 'DELETE' }),
  getAvisosManutencaoMaquinario: () => request('/maquinario/manutencoes/avisos'),
  getCountAvisosManutencaoMaquinario: () => request('/maquinario/manutencoes/avisos/count'),

  // OCR
  uploadTicket: async (file) => {
    const form = new FormData();
    form.append('foto', file);
    const token = getToken();
    const headers = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const res = await fetch(API + '/ticket/ocr', { method: 'POST', body: form, headers });
    if (!res.ok) throw new Error(`Erro OCR: ${res.status}`);
    return res.json();
  },
};
